import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { COURS_VOITURE_ELEVE, TOTAL_LECONS } from '../data/coursVoitureEleve';
import { useAuth } from '../context/AuthContext';
import { ChevronDown, ChevronRight, Target, AlertTriangle, Sparkles, Clock, BookOpenCheck, ArrowLeft, GraduationCap, ListChecks, ShieldCheck, Copy, Check, X, ClipboardList, Brain, ListTodo, Eye, EyeOff, RotateCcw, MessageSquare, Send } from 'lucide-react';
import { sparkle, celebrate } from '../lib/celebrate';
import { toTu, toTuFlat } from '../lib/toTu';

const SEANCE_STORAGE_KEY = 'cv_seance_selection_v1';
const REVOIR_STORAGE_KEY = 'cv_revoir_selection_v1';

function getNextLesson(competenceCode, idx) {
  const compIdx = COURS_VOITURE_ELEVE.findIndex((c) => c.code === competenceCode);
  if (compIdx < 0) return null;
  const currentComp = COURS_VOITURE_ELEVE[compIdx];
  // Next lesson in same competence
  if (idx + 1 < currentComp.lecons.length) {
    return {
      code: currentComp.code,
      idx: idx + 1,
      titre: currentComp.lecons[idx + 1].titre,
    };
  }
  // Next competence, first lesson
  const nextComp = COURS_VOITURE_ELEVE[compIdx + 1];
  if (nextComp && nextComp.lecons.length > 0) {
    return {
      code: nextComp.code,
      idx: 0,
      titre: nextComp.lecons[0].titre,
    };
  }
  return null;
}

// Format "Copier" — RÉSUMÉ court pour le livret élève.
// 1 ligne concise par section REMC (Pourquoi / Comment / Risques / Influences / Pressions).
// Objectif : contenu synthétique, pas de "tartine" à coller.
function truncate(s, max = 220) {
  if (!s) return '';
  const t = String(s).trim();
  if (t.length <= max) return t;
  return t.slice(0, max).replace(/[,;.:]?\s+\S*$/, '') + '…';
}

function buildLeconPlainText({ competenceCode, idx, lecon, includeNext = true, isRevoir = false }) {
  const lines = [];
  const header = isRevoir
    ? `Ce jour, nous avons abordé la leçon ${competenceCode}.${idx + 1} — ${lecon.titre} (compétence à revoir).`
    : `Ce jour, nous avons abordé la leçon ${competenceCode}.${idx + 1} — ${lecon.titre}.`;
  lines.push(header);

  const r = lecon.remc;
  const p = r?.pourquoi;
  const c = r?.comment;
  const ri = r?.risques;
  const inf = r?.influences;
  const pr = r?.pressions;

  // POURQUOI — résumé : objectif de la leçon (déjà 1 ligne)
  if (lecon.objectif || p?.intro) {
    lines.push('');
    lines.push('Pourquoi : ' + truncate(toTu(lecon.objectif || p.intro)));
  }

  // COMMENT — résumé : "à traiter" (liste de topics) ou 1er savoir-faire
  if (c) {
    let commentLine = c.a_traiter || (c.savoir_faire && c.savoir_faire[0]) || '';
    if (commentLine) {
      lines.push('');
      lines.push('Comment : ' + truncate(toTu(commentLine)));
    }
  }

  // RISQUES — résumé : risques_listes (déjà synthétique) ou 1re situation
  if (ri) {
    let risquesLine = ri.risques_listes || (ri.situations && ri.situations[0]) || ri.cas_observe || '';
    if (risquesLine) {
      lines.push('');
      lines.push('Risques : ' + truncate(toTu(risquesLine)));
    }
  }

  // INFLUENCES — résumé : 1re question clé (invite à la réflexion)
  if (inf?.questions?.length > 0) {
    lines.push('');
    lines.push('Influences : ' + truncate(toTu(inf.questions[0])));
  }

  // PRESSIONS — résumé : 1re situation ou intro raccourcie
  if (pr) {
    let pressionsLine = '';
    if (pr.situations?.length > 0) pressionsLine = pr.situations[0];
    else if (pr.situations_groupees?.length > 0) {
      pressionsLine = pr.situations_groupees.map(g => `${g.titre} : ${g.items}`).join(' · ');
    } else if (pr.intro) pressionsLine = pr.intro;
    if (pressionsLine) {
      lines.push('');
      lines.push('Pressions : ' + truncate(toTu(pressionsLine)));
    }
  }

  if (includeNext) {
    lines.push('');
    if (isRevoir) {
      lines.push(`Prochaine séance : reprendre la leçon ${competenceCode}.${idx + 1} pour validation.`);
    } else {
      const next = getNextLesson(competenceCode, idx);
      if (next) lines.push(`Prochaine leçon : ${next.code}.${next.idx + 1} — ${next.titre}.`);
    }
  }
  return lines.join('\n');
}

function buildSeanceText(selectedItems, revoirIds = new Set()) {
  const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const out = [];
  out.push(`Séance du ${dateStr}`);
  out.push('');
  out.push('Ce jour, nous avons abordé :');
  const aRevoir = [];
  selectedItems.forEach((item) => {
    const id = `${item.competenceCode}.${item.idx}`;
    const revoir = revoirIds.has(id);
    if (revoir) aRevoir.push(item);
    out.push(`- ${item.competenceCode}.${item.idx + 1} · ${item.lecon.titre}${revoir ? ' (à revoir)' : ''}`);
  });
  if (aRevoir.length > 0) {
    out.push('');
    out.push('Prochaine séance, on reprend :');
    aRevoir.forEach((item) => {
      out.push(`- ${item.competenceCode}.${item.idx + 1} · ${item.lecon.titre}`);
    });
    const last = selectedItems[selectedItems.length - 1];
    if (last) {
      const next = getNextLesson(last.competenceCode, last.idx);
      if (next) {
        out.push('');
        out.push(`Puis : ${next.code}.${next.idx + 1} · ${next.titre}.`);
      }
    }
  } else {
    const last = selectedItems[selectedItems.length - 1];
    if (last) {
      const next = getNextLesson(last.competenceCode, last.idx);
      if (next) {
        out.push('');
        out.push(`Prochaine leçon : ${next.code}.${next.idx + 1} · ${next.titre}.`);
      }
    }
  }
  return out.join('\n');
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch (err) {
    console.warn('Copy failed:', err);
    return false;
  }
}

function ProgressBar({ pct }) {
  return (
    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

function RemcSection({ icon: Icon, color, title, children }) {
  return (
    <div className={`rounded-lg border p-3 ${color}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} className="shrink-0" />
        <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold font-mono-spec">{title}</div>
      </div>
      <div className="space-y-2 text-[13px] text-white/80 leading-relaxed">{children}</div>
    </div>
  );
}

function RemcBlock({ remc, competenceCode, letter }) {
  const [open, setOpen] = useState(false);
  const r = remc;
  return (
    <div className="rounded-xl border border-sky-700/30 bg-sky-950/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-sky-950/20 transition"
        aria-expanded={open}
        data-testid={`remc-toggle-${competenceCode}-${letter}`}
      >
        <span className="shrink-0 w-8 h-8 rounded-lg bg-sky-500/20 border border-sky-500/40 flex items-center justify-center text-sky-300 font-mono-spec text-[11px] font-bold">
          {competenceCode}{letter}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sky-200 text-[13.5px] font-semibold">Trame REMC officielle du livret</div>
          <div className="text-white/50 text-[11.5px]">POURQUOI · COMMENT · RISQUES · INFLUENCES · PRESSIONS</div>
        </div>
        <ChevronDown size={16} className={`text-sky-300 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="p-3.5 pt-1 space-y-3 border-t border-sky-700/20">
          {/* POURQUOI */}
          {r.pourquoi && (
            <RemcSection icon={Target} color="border-sky-600/30 bg-sky-950/15 text-sky-300" title="Pourquoi (raisons)">
              {r.pourquoi.intro && <p className="italic text-white/70">{toTu(r.pourquoi.intro)}</p>}
              {/* Format Q/R multi-questions (livret fidèle) */}
              {r.pourquoi.qr?.length > 0 && r.pourquoi.qr.map((qr, i) => (
                <div key={i} className="mt-2">
                  <div className="font-semibold text-white">{qr.q}</div>
                  {Array.isArray(qr.r) ? (
                    <ul className="mt-1 space-y-0.5">
                      {qr.r.map((rr, j) => <li key={j} className="flex gap-2"><span className="text-sky-400">•</span><span>{toTuFlat(rr)}</span></li>)}
                    </ul>
                  ) : qr.r ? (
                    <p className="mt-1 text-white/80">{toTu(qr.r)}</p>
                  ) : null}
                </div>
              ))}
              {/* Format simple : question_avantages + avantages / question_inconvenients + inconvenients */}
              {r.pourquoi.question_avantages && <div className="mt-2 font-semibold text-white">{toTu(r.pourquoi.question_avantages)}</div>}
              {r.pourquoi.avantages?.length > 0 && (
                <ul className="space-y-1">
                  {r.pourquoi.avantages.map((a, i) => <li key={i} className="flex gap-2"><span className="text-sky-400">•</span><span>{toTuFlat(a)}</span></li>)}
                </ul>
              )}
              {r.pourquoi.question_inconvenients && <div className="mt-2 font-semibold text-white">{toTu(r.pourquoi.question_inconvenients)}</div>}
              {r.pourquoi.inconvenients?.length > 0 && (
                <ul className="space-y-1">
                  {r.pourquoi.inconvenients.map((a, i) => <li key={i} className="flex gap-2"><span className="text-sky-400">•</span><span>{toTuFlat(a)}</span></li>)}
                </ul>
              )}
            </RemcSection>
          )}
          {/* COMMENT */}
          {r.comment && (
            <RemcSection icon={ListChecks} color="border-emerald-600/30 bg-emerald-950/15 text-emerald-300" title="Comment (procédures)">
              {r.comment.a_traiter && <div><span className="inline-block px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-[11px] font-mono-spec mr-2">À traiter</span><span className="text-white/85">{toTu(r.comment.a_traiter)}</span></div>}
              {/* Questions ouvertes (livret fidèle) */}
              {r.comment.questions_ouvertes?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {r.comment.questions_ouvertes.map((q, i) => (
                    <div key={i} className="flex gap-2 text-white font-semibold"><span className="text-emerald-400">?</span><span>{toTuFlat(q)}</span></div>
                  ))}
                </div>
              )}
              {r.comment.question_freq_int && (
                <div className="mt-2">
                  <div className="font-semibold text-white">{toTu(r.comment.question_freq_int)}</div>
                  {r.comment.choix_freq_int?.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {r.comment.choix_freq_int.map((c, i) => <li key={i} className="flex gap-2"><span className="text-emerald-400">•</span><span>{c}</span></li>)}
                    </ul>
                  )}
                </div>
              )}
              {r.comment.question_freq_ext && (
                <div className="mt-2">
                  <div className="font-semibold text-white">{toTu(r.comment.question_freq_ext)}</div>
                  {r.comment.choix_freq_ext?.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {r.comment.choix_freq_ext.map((c, i) => <li key={i} className="flex gap-2"><span className="text-emerald-400">•</span><span>{c}</span></li>)}
                    </ul>
                  )}
                </div>
              )}
              {r.comment.info_plus && (
                <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-950/25 p-2.5">
                  <div className="inline-block px-2 py-0.5 rounded bg-emerald-500/30 border border-emerald-500/50 text-emerald-100 text-[10.5px] font-mono-spec font-bold mb-2">INFO+</div>
                  {r.comment.info_plus.regulieres && (
                    <div className="mt-1.5">
                      <div className="font-semibold text-white text-[12.5px]">{r.comment.info_plus.regulieres.titre}</div>
                      <ul className="mt-1 space-y-0.5">
                        {r.comment.info_plus.regulieres.items.map((c, i) => <li key={i} className="flex gap-2 text-[12.5px]"><span className="text-emerald-400">•</span><span>{c}</span></li>)}
                      </ul>
                    </div>
                  )}
                  {r.comment.info_plus.tous_2000km && (
                    <div className="mt-2">
                      <div className="font-semibold text-white text-[12.5px]">{r.comment.info_plus.tous_2000km.titre}</div>
                      <ul className="mt-1 space-y-0.5">
                        {r.comment.info_plus.tous_2000km.items.map((c, i) => <li key={i} className="flex gap-2 text-[12.5px]"><span className="text-emerald-400">•</span><span>{c}</span></li>)}
                      </ul>
                    </div>
                  )}
                  {r.comment.info_plus.tous_20000km && (
                    <div className="mt-2">
                      <div className="font-semibold text-white text-[12.5px]">{r.comment.info_plus.tous_20000km.titre}</div>
                      <ul className="mt-1 space-y-0.5">
                        {r.comment.info_plus.tous_20000km.items.map((c, i) => <li key={i} className="flex gap-2 text-[12.5px]"><span className="text-emerald-400">•</span><span>{c}</span></li>)}
                      </ul>
                    </div>
                  )}
                  {r.comment.info_plus.deformation && (
                    <div className="mt-2">
                      <div className="font-semibold text-white text-[12.5px]">{r.comment.info_plus.deformation.titre}</div>
                      <ul className="mt-1 space-y-0.5">
                        {r.comment.info_plus.deformation.items.map((c, i) => <li key={i} className="flex gap-2 text-[12.5px]"><span className="text-emerald-400">•</span><span>{c}</span></li>)}
                      </ul>
                    </div>
                  )}
                  {r.comment.info_plus.champ_visuel && (
                    <div className="mt-2">
                      <div className="font-semibold text-white text-[12.5px]">{r.comment.info_plus.champ_visuel.titre}</div>
                      <ul className="mt-1 space-y-0.5">
                        {r.comment.info_plus.champ_visuel.items.map((c, i) => <li key={i} className="flex gap-2 text-[12.5px]"><span className="text-emerald-400">•</span><span>{c}</span></li>)}
                      </ul>
                    </div>
                  )}
                  {r.comment.info_plus.tache_mariotte && (
                    <div className="mt-2 text-[12.5px] text-white/85"><span className="font-semibold text-white">Tache de Mariotte : </span>{r.comment.info_plus.tache_mariotte.replace(/^Tache de Mariotte : /, '')}</div>
                  )}
                  {r.comment.info_plus.exercice && (
                    <div className="mt-2 rounded border border-emerald-500/20 bg-emerald-950/25 p-2 text-[12.5px] text-white/85 italic">{r.comment.info_plus.exercice}</div>
                  )}
                  {r.comment.info_plus.boite_manuelle && (
                    <div className="mt-2">
                      <div className="font-semibold text-white text-[12.5px]">{r.comment.info_plus.boite_manuelle.titre}</div>
                      <ul className="mt-1 space-y-0.5">
                        {r.comment.info_plus.boite_manuelle.items.map((c, i) => <li key={i} className="flex gap-2 text-[12.5px]"><span className="text-emerald-400">•</span><span>{c}</span></li>)}
                      </ul>
                    </div>
                  )}
                  {r.comment.info_plus.boite_automatique && (
                    <div className="mt-2">
                      <div className="font-semibold text-white text-[12.5px]">{r.comment.info_plus.boite_automatique.titre}</div>
                      <ul className="mt-1 space-y-0.5">
                        {r.comment.info_plus.boite_automatique.items.map((c, i) => <li key={i} className="flex gap-2 text-[12.5px]"><span className="text-emerald-400">•</span><span>{c}</span></li>)}
                      </ul>
                    </div>
                  )}
                  {r.comment.info_plus.conclusion && (
                    <p className="mt-2 text-[12.5px] text-white/80 italic">{r.comment.info_plus.conclusion}</p>
                  )}
                  {r.comment.info_plus.securite_passive && (
                    <div className="mt-2">
                      <div className="font-semibold text-white text-[12.5px]">{r.comment.info_plus.securite_passive.titre}</div>
                      <p className="mt-1 text-[12.5px] text-white/85">{r.comment.info_plus.securite_passive.elements}</p>
                    </div>
                  )}
                  {r.comment.info_plus.lecture_pneu && (
                    <div className="mt-2 text-[12px] italic text-white/70">{r.comment.info_plus.lecture_pneu}</div>
                  )}
                </div>
              )}
              {r.comment.savoir?.length > 0 && (
                <div className="mt-2">
                  <div className="font-semibold text-white">Ce que tu dois savoir</div>
                  <ul className="mt-1 space-y-0.5">
                    {r.comment.savoir.map((c, i) => <li key={i} className="flex gap-2"><span className="text-emerald-400">?</span><span>{toTuFlat(c)}</span></li>)}
                  </ul>
                </div>
              )}
              {r.comment.savoir_faire?.length > 0 && (
                <div className="mt-2">
                  <div className="font-semibold text-white">Ce que tu dois savoir-faire</div>
                  <ul className="mt-1 space-y-0.5">
                    {r.comment.savoir_faire.map((c, i) => <li key={i} className="flex gap-2"><span className="text-emerald-400">•</span><span>{toTuFlat(c)}</span></li>)}
                  </ul>
                </div>
              )}
            </RemcSection>
          )}
          {/* RISQUES */}
          {r.risques && (
            <RemcSection icon={AlertTriangle} color="border-amber-600/30 bg-amber-950/15 text-amber-300" title="Risques">
              {r.risques.intro && <p className="italic text-white/70">{toTu(r.risques.intro)}</p>}
              {/* Format livret fidèle : directives + cas observé + risques listés */}
              {r.risques.directives?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {r.risques.directives.map((c, i) => <li key={i} className="flex gap-2"><span className="text-amber-400">→</span><span className="text-white/85">{toTuFlat(c)}</span></li>)}
                </ul>
              )}
              {r.risques.cas_observe && (
                <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-950/25 p-2.5">
                  <div className="inline-block px-2 py-0.5 rounded bg-amber-500/30 border border-amber-500/50 text-amber-100 text-[10.5px] font-mono-spec font-bold mb-1.5">Cas observé</div>
                  <p className="text-[12.5px] text-white/85">{toTu(r.risques.cas_observe)}</p>
                </div>
              )}
              {r.risques.risques_listes && (
                <div className="mt-2">
                  <div className="font-semibold text-white">Risques :</div>
                  <p className="mt-1 text-white/85">{toTu(r.risques.risques_listes)}</p>
                </div>
              )}
              {/* Format auto-généré : questions + situations */}
              {r.risques.questions?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {r.risques.questions.map((c, i) => <li key={i} className="flex gap-2"><span className="text-amber-400">?</span><span className="font-semibold text-white">{toTuFlat(c)}</span></li>)}
                </ul>
              )}
              {r.risques.situations?.length > 0 && (
                <ul className="mt-2 space-y-1.5">
                  {r.risques.situations.map((c, i) => <li key={i} className="flex gap-2"><span className="text-amber-400">•</span><span>{toTuFlat(c)}</span></li>)}
                </ul>
              )}
            </RemcSection>
          )}
          {/* INFLUENCES */}
          {r.influences && (
            <RemcSection icon={Brain} color="border-violet-600/30 bg-violet-950/15 text-violet-300" title="Influences (entourage, mode de vie)">
              {r.influences.intro && <p className="italic text-white/70">{toTu(r.influences.intro)}</p>}
              {r.influences.definition && <p className="mt-2 text-[12.5px] text-white/70 border-l-2 border-violet-500/40 pl-3 italic">{toTu(r.influences.definition)}</p>}
              {r.influences.questions?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {r.influences.questions.map((c, i) => <li key={i} className="flex gap-2"><span className="text-violet-400">?</span><span>{toTuFlat(c)}</span></li>)}
                </ul>
              )}
            </RemcSection>
          )}
          {/* PRESSIONS */}
          {r.pressions && (
            <RemcSection icon={Eye} color="border-rose-600/30 bg-rose-950/15 text-rose-300" title="Pressions (publicité, travail, société)">
              {r.pressions.intro && <p className="italic text-white/70">{toTu(r.pressions.intro)}</p>}
              {r.pressions.nb && <p className="mt-2 text-white/70 italic border-l-2 border-rose-500/40 pl-3">{toTu(r.pressions.nb)}</p>}
              {r.pressions.question_situations && <div className="mt-2 font-semibold text-white">{toTu(r.pressions.question_situations)}</div>}
              {r.pressions.situations?.length > 0 && (
                <ul className="mt-1 space-y-1">
                  {r.pressions.situations.map((c, i) => <li key={i} className="flex gap-2"><span className="text-rose-400">•</span><span>{toTuFlat(c)}</span></li>)}
                </ul>
              )}
              {/* Format livret : situations groupées par titre */}
              {r.pressions.situations_groupees?.length > 0 && (
                <div className="mt-1 space-y-1.5">
                  {r.pressions.situations_groupees.map((g, i) => (
                    <div key={i}>
                      <span className="font-semibold text-rose-200">{g.titre} :</span> <span className="text-white/85">{g.items}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Info+ légal (article R 412-1) */}
              {r.pressions.info_legal && (
                <div className="mt-2 rounded-lg border border-rose-500/30 bg-rose-950/25 p-2.5">
                  <div className="inline-block px-2 py-0.5 rounded bg-rose-500/30 border border-rose-500/50 text-rose-100 text-[10.5px] font-mono-spec font-bold mb-1.5">INFO+ LÉGAL</div>
                  <div className="font-semibold text-white text-[12.5px]">{r.pressions.info_legal.titre}</div>
                  <ul className="mt-1 space-y-0.5">
                    {r.pressions.info_legal.items.map((c, i) => <li key={i} className="flex gap-2 text-[12.5px]"><span className="text-rose-400">•</span><span>{c}</span></li>)}
                  </ul>
                </div>
              )}
            </RemcSection>
          )}
        </div>
      )}
    </div>
  );
}

function LessonCard({ lecon, competenceCode, competenceTitle, idx, isInSeance, onToggleSeance, isRevoir, onToggleRevoir, targetKey }) {
  const leconId = `${competenceCode}.${idx}`;
  const isTarget = targetKey === leconId;
  const [open, setOpen] = useState(isTarget);
  const [copied, setCopied] = useState(false);
  const cardRef = React.useRef(null);

  useEffect(() => {
    if (isTarget) {
      setOpen(true);
      // Scroll into view after mount so the auto-expanded lesson lands centered
      const t = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [isTarget]);

  const handleCopy = async (e) => {
    e.stopPropagation();
    const text = buildLeconPlainText({ competenceCode, competenceTitle, idx, lecon, isRevoir });
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      sparkle();
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const handleShareSMS = (e) => {
    e.stopPropagation();
    const text = buildLeconPlainText({ competenceCode, competenceTitle, idx, lecon, isRevoir });
    // sms: URI works on iOS, Android accepts both sms:?body= and sms:?&body=
    window.location.href = `sms:?&body=${encodeURIComponent(text)}`;
    sparkle();
  };

  const handleShareWhatsApp = (e) => {
    e.stopPropagation();
    const text = buildLeconPlainText({ competenceCode, competenceTitle, idx, lecon, isRevoir });
    // wa.me ouvre WhatsApp Web ou l'app native selon le device
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    sparkle();
  };

  return (
    <div ref={cardRef} className={`rounded-xl border overflow-hidden transition-colors ${isTarget ? 'ring-2 ring-sky-500/60 ring-offset-2 ring-offset-[#0a0a0b]' : ''} ${isRevoir ? 'border-amber-500/50 bg-amber-950/10' : isInSeance ? 'border-emerald-500/50 bg-emerald-950/10' : 'border-white/10 bg-[#0d0d0e] hover:border-red-700/40'}`} data-testid={`lecon-${competenceCode}-${lecon.i}`}>
      <div className="w-full flex items-center gap-2 sm:gap-4 p-4">
        {/* Checkbox séance */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSeance(leconId); }}
          aria-label={isInSeance ? 'Retirer de la séance du jour' : 'Ajouter à la séance du jour'}
          aria-pressed={isInSeance}
          data-testid={`seance-toggle-${competenceCode}-${lecon.i}`}
          className={`shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition ${
            isInSeance
              ? 'border-emerald-500 bg-emerald-500 text-black'
              : 'border-white/25 hover:border-emerald-400 bg-transparent'
          }`}
          title={isInSeance ? 'Dans la séance' : 'Ajouter à la séance'}
        >
          {isInSeance && <Check size={14} strokeWidth={3} />}
        </button>

        {/* Toggle open lesson */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex-1 min-w-0 flex items-center gap-3 sm:gap-4 text-left"
          aria-expanded={open}
        >
          <span className="shrink-0 w-10 h-10 rounded-lg bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 font-mono-spec text-[13px] font-bold">
            {competenceCode}.{idx + 1}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-white font-serif-editorial text-[17px] lg:text-[18px] leading-tight flex items-center gap-2 flex-wrap">
              <span>{lecon.titre}</span>
              {isRevoir && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10px] font-mono-spec uppercase tracking-wider">
                  <RotateCcw size={9} /> À revoir
                </span>
              )}
            </div>
            <div className="text-white/55 text-[12.5px] mt-0.5 line-clamp-1">{lecon.objectif}</div>
          </div>
          <ChevronDown size={16} className={`text-white/50 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="px-4 pb-5 pt-1 border-t border-white/5 space-y-4">
          {/* Objectif */}
          <div className="flex items-start gap-2.5">
            <Target size={14} className="text-red-400 shrink-0 mt-1" />
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-red-400 font-bold font-mono-spec mb-1">Objectif pédagogique</div>
              <p className="text-white/80 text-[13.5px] leading-relaxed">{lecon.objectif}</p>
            </div>
          </div>

          {/* Points-clés */}
          {lecon.points?.length > 0 && (
            <div className="flex items-start gap-2.5">
              <ListChecks size={14} className="text-emerald-400 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-400 font-bold font-mono-spec mb-1.5">Points-clés à mémoriser</div>
                <ul className="space-y-1.5">
                  {lecon.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/75 text-[13px]">
                      <span className="text-emerald-400 shrink-0 mt-1.5 w-1 h-1 rounded-full bg-emerald-400 block" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Erreurs */}
          {lecon.erreurs?.length > 0 && (
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-amber-400 font-bold font-mono-spec mb-1.5">Erreurs fréquentes</div>
                <ul className="space-y-1.5">
                  {lecon.erreurs.map((e, i) => (
                    <li key={i} className="text-white/75 text-[13px] leading-relaxed">{e}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Geste pro */}
          {lecon.geste && (
            <div className="rounded-lg border border-red-700/30 bg-red-950/15 p-3 flex items-start gap-2.5">
              <Sparkles size={14} className="text-red-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-red-300 font-bold font-mono-spec mb-1">Geste de moniteur ECSR</div>
                <p className="text-white/85 text-[13px] italic leading-relaxed">{lecon.geste}</p>
              </div>
            </div>
          )}

          {/* Mnémotechnique */}
          {lecon.mnemo && (
            <div className="rounded-lg border border-violet-600/30 bg-violet-950/15 p-3 flex items-start gap-2.5">
              <Brain size={14} className="text-violet-300 shrink-0 mt-0.5" />
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-violet-300 font-bold font-mono-spec mb-1">Mnémotechnique</div>
                <p className="text-white/85 text-[13px] leading-relaxed">{lecon.mnemo}</p>
              </div>
            </div>
          )}

          {/* Check-list d'auto-évaluation */}
          {lecon.checklist?.length > 0 && (
            <div className="flex items-start gap-2.5">
              <ListTodo size={14} className="text-sky-400 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-sky-400 font-bold font-mono-spec mb-1.5">Check-list d'auto-évaluation</div>
                <ul className="space-y-1.5">
                  {lecon.checklist.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/75 text-[13px]">
                      <span className="text-sky-400/70 shrink-0 mt-0.5 font-mono-spec">☐</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Points d'attention terrain */}
          {lecon.attention?.length > 0 && (
            <div className="rounded-lg border border-amber-600/25 bg-amber-950/10 p-3 flex items-start gap-2.5">
              <Eye size={14} className="text-amber-300 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-amber-300 font-bold font-mono-spec mb-1.5">Points d'attention terrain</div>
                <ul className="space-y-1.5">
                  {lecon.attention.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/80 text-[13px] leading-relaxed">
                      <span className="text-amber-300/70 shrink-0 mt-0.5">!</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Trame REMC officielle du livret pédagogique */}
          {lecon.remc && <RemcBlock remc={lecon.remc} competenceCode={competenceCode} letter={lecon.letter} />}

          {/* Copy toolbar */}
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-white/5">
            <button
              type="button"
              onClick={handleCopy}
              data-testid={`copy-lecon-${competenceCode}-${lecon.i}`}
              className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[12.5px] font-semibold transition ${
                copied
                  ? 'bg-emerald-600/20 border border-emerald-500/40 text-emerald-300'
                  : 'bg-white/5 border border-white/10 hover:border-red-700/40 hover:bg-red-950/20 text-white/80 hover:text-white'
              }`}
              aria-label="Copier le contenu de la leçon"
            >
              {copied ? <><Check size={14} /> Copié — colle dans le livret</> : <><Copy size={14} /> Copier cette leçon</>}
            </button>
            <button
              type="button"
              onClick={handleShareSMS}
              data-testid={`sms-lecon-${competenceCode}-${lecon.i}`}
              className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[12.5px] font-semibold transition bg-white/5 border border-white/10 hover:border-sky-500/50 hover:bg-sky-950/20 text-white/80 hover:text-sky-200"
              aria-label="Envoyer la leçon par SMS"
              title="Ouvre l'app SMS de ton téléphone avec la leçon pré-remplie"
            >
              <MessageSquare size={14} /> SMS
            </button>
            <button
              type="button"
              onClick={handleShareWhatsApp}
              data-testid={`whatsapp-lecon-${competenceCode}-${lecon.i}`}
              className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[12.5px] font-semibold transition bg-white/5 border border-white/10 hover:border-[#25D366]/60 hover:bg-[#25D366]/10 text-white/80 hover:text-[#25D366]"
              aria-label="Envoyer la leçon par WhatsApp"
              title="Ouvre WhatsApp avec la leçon pré-remplie"
            >
              <Send size={14} /> WhatsApp
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleSeance(leconId); }}
              data-testid={`seance-toggle-expanded-${competenceCode}-${lecon.i}`}
              className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[12.5px] font-semibold transition ${
                isInSeance
                  ? 'bg-emerald-600/20 border border-emerald-500/40 text-emerald-300'
                  : 'bg-white/5 border border-white/10 hover:border-emerald-500/40 hover:bg-emerald-950/20 text-white/80 hover:text-white'
              }`}
            >
              {isInSeance ? <><Check size={14} /> Dans la séance</> : <><ClipboardList size={14} /> Ajouter à la séance</>}
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleRevoir(leconId); }}
              data-testid={`revoir-toggle-${competenceCode}-${lecon.i}`}
              aria-pressed={isRevoir}
              className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[12.5px] font-semibold transition ${
                isRevoir
                  ? 'bg-amber-500/20 border border-amber-500/50 text-amber-300'
                  : 'bg-white/5 border border-white/10 hover:border-amber-500/40 hover:bg-amber-950/20 text-white/80 hover:text-white'
              }`}
              title={isRevoir ? 'Compétence marquée à revoir' : 'Marquer la leçon à revoir (non validée)'}
            >
              {isRevoir ? <><RotateCcw size={14} /> Compétence à revoir</> : <><RotateCcw size={14} /> À revoir</>}
            </button>
            <span className="text-[11px] text-white/40 font-mono-spec">Texte brut · à coller dans le livret ou tout traitement de texte</span>
          </div>
        </div>
      )}
    </div>
  );
}

function CompetenceBlock({ comp, seanceIds, onToggleSeance, revoirIds, onToggleRevoir, targetKey }) {
  return (
    <section className="mt-12" data-testid={`competence-${comp.code}`}>
      {/* Header compétence */}
      <div className="rounded-xl sm:rounded-2xl border border-red-700/30 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-4 sm:p-6 lg:p-7 mb-4 sm:mb-5">
        <div className="flex items-start gap-3 sm:gap-5">
          <div className="shrink-0 w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-red-600 text-white font-serif-editorial text-[24px] sm:text-[34px] lg:text-[42px] flex items-center justify-center shadow-lg shadow-red-900/40">
            {comp.code}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-1">Compétence REMC · {comp.lecons.length} leçons</div>
            <h2 className="font-serif-editorial text-white text-[20px] sm:text-[26px] lg:text-[34px] leading-[1.1] sm:leading-[1.05]">{comp.title}</h2>
            <p className="text-white/65 text-[12.5px] sm:text-[14px] mt-2 sm:mt-3 leading-relaxed">{comp.intro}</p>
            <div className="mt-4 flex flex-wrap gap-2 text-[11.5px] font-mono-spec">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-white/70">
                <Clock size={11} /> {comp.duree}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-white/70">
                <ShieldCheck size={11} /> Pré-requis : {comp.pre_requis}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Leçons */}
      <div className="space-y-2.5">
        {comp.lecons.map((l, i) => (
          <LessonCard
            key={l.i}
            lecon={l}
            competenceCode={comp.code}
            competenceTitle={comp.title}
            idx={i}
            isInSeance={seanceIds.has(`${comp.code}.${i}`)}
            onToggleSeance={onToggleSeance}
            isRevoir={revoirIds.has(`${comp.code}.${i}`)}
            onToggleRevoir={onToggleRevoir}
            targetKey={targetKey}
          />
        ))}
      </div>
    </section>
  );
}

export default function CoursVoitureElevePage() {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [searchParams] = useSearchParams();

  // Deep-link cible : ?remc=C1b → { competence: 'C1', leconId: 'C1.1' }
  const targetKey = useMemo(() => {
    const raw = searchParams.get('remc');
    if (!raw || raw.length < 3) return null;
    const compCode = raw.slice(0, 2).toUpperCase();
    const letter = raw.slice(2).toLowerCase();
    const comp = COURS_VOITURE_ELEVE.find((c) => c.code === compCode);
    if (!comp) return null;
    const idx = comp.lecons.findIndex((l) => l.letter === letter);
    if (idx < 0) return null;
    return `${compCode}.${idx}`;
  }, [searchParams]);

  // Auto-filtre sur la compétence ciblée pour rapprocher visuellement la leçon
  useEffect(() => {
    if (targetKey) {
      const compCode = targetKey.split('.')[0];
      setFilter(compCode);
    }
  }, [targetKey]);

  // Séance du jour: Set d'IDs "C1.3" persistés en localStorage
  const [seanceIds, setSeanceIds] = useState(() => {
    try {
      const raw = localStorage.getItem(SEANCE_STORAGE_KEY);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch { return new Set(); }
  });

  // Leçons à revoir (compétences non validées) : Set d'IDs "C1.3" persistés
  const [revoirIds, setRevoirIds] = useState(() => {
    try {
      const raw = localStorage.getItem(REVOIR_STORAGE_KEY);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch { return new Set(); }
  });
  const [seanceCopied, setSeanceCopied] = useState(false);
  const [seancePanelExpanded, setSeancePanelExpanded] = useState(false);
  const [seancePreviewOpen, setSeancePreviewOpen] = useState(false);
  const [previewCopied, setPreviewCopied] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(SEANCE_STORAGE_KEY, JSON.stringify(Array.from(seanceIds))); } catch {}
  }, [seanceIds]);

  useEffect(() => {
    try { localStorage.setItem(REVOIR_STORAGE_KEY, JSON.stringify(Array.from(revoirIds))); } catch {}
  }, [revoirIds]);

  const toggleSeance = useCallback((leconId) => {
    setSeanceIds((prev) => {
      const next = new Set(prev);
      if (next.has(leconId)) next.delete(leconId); else next.add(leconId);
      return next;
    });
  }, []);

  const toggleRevoir = useCallback((leconId) => {
    setRevoirIds((prev) => {
      const next = new Set(prev);
      if (next.has(leconId)) next.delete(leconId); else next.add(leconId);
      return next;
    });
  }, []);

  const clearSeance = () => {
    setSeanceIds(new Set());
    setSeancePanelExpanded(false);
  };

  // Items sélectionnés dans l'ordre de la page (C1.1 puis C1.2 puis C2.1 …)
  const selectedItems = useMemo(() => {
    const list = [];
    COURS_VOITURE_ELEVE.forEach((comp) => {
      comp.lecons.forEach((lecon, i) => {
        const id = `${comp.code}.${i}`;
        if (seanceIds.has(id)) {
          list.push({ competenceCode: comp.code, competenceTitle: comp.title, idx: i, lecon });
        }
      });
    });
    return list;
  }, [seanceIds]);

  const copySeance = async () => {
    if (selectedItems.length === 0) return;
    const text = buildSeanceText(selectedItems, revoirIds);
    const ok = await copyToClipboard(text);
    if (ok) {
      setSeanceCopied(true);
      celebrate();
      setTimeout(() => setSeanceCopied(false), 2200);
    }
  };

  const smsSeance = () => {
    if (selectedItems.length === 0) return;
    const text = buildSeanceText(selectedItems, revoirIds);
    window.location.href = `sms:?&body=${encodeURIComponent(text)}`;
    sparkle();
  };

  const whatsappSeance = () => {
    if (selectedItems.length === 0) return;
    const text = buildSeanceText(selectedItems, revoirIds);
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    sparkle();
  };

  const visibleComps = useMemo(() => {
    if (filter === 'all') return COURS_VOITURE_ELEVE;
    return COURS_VOITURE_ELEVE.filter((c) => c.code === filter);
  }, [filter]);

  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-4 lg:px-6 mt-10" data-testid="cours-voiture-eleve-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6" aria-label="Fil d'Ariane">
          <Link to="/" className="hover:text-white transition">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/cours-interactifs" className="hover:text-white transition">Cours interactifs</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Cours en voiture · Élève</span>
        </nav>

        {/* Retour à la progression si arrivée via deep-link */}
        {targetKey && (
          <Link
            to="/cours/progression"
            data-testid="back-to-progression"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 hover:border-emerald-400 text-[12.5px] font-semibold mb-6 transition"
          >
            <ArrowLeft size={13} /> Retour à ma progression
          </Link>
        )}

        {/* Hero */}
        <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-7 lg:p-10 mb-7 sm:mb-10 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-red-600/10 blur-3xl" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-3 sm:mb-4">
              <GraduationCap size={13} className="text-red-400" />
              <span className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">Espace élève · Cours en voiture</span>
            </div>
            <h1 className="font-serif-editorial text-white text-[26px] sm:text-[44px] lg:text-[58px] leading-[1.02] sm:leading-[0.98] tracking-tight">
              Apprends à conduire,<br />
              <span className="text-red-500">pas à cocher des cases.</span>
            </h1>
            <p className="text-white/70 text-[13.5px] sm:text-[15px] lg:text-[16px] mt-3 sm:mt-5 max-w-2xl leading-relaxed">
              {TOTAL_LECONS} leçons pédagogiques harmonisées <span className="text-white">REMC</span>, conçues par des moniteurs ECSR. Objectifs, points-clés, erreurs à éviter, gestes de moniteur : tout ce qu'il faut comprendre avant chaque heure derrière le volant.
            </p>

            {/* Stats strip */}
            <div className="mt-5 sm:mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
              {COURS_VOITURE_ELEVE.map((c) => (
                <button
                  key={c.code}
                  onClick={() => setFilter(filter === c.code ? 'all' : c.code)}
                  data-testid={`filter-${c.code}`}
                  className={`rounded-xl border p-2.5 sm:p-3 text-left transition ${filter === c.code ? 'border-red-500 bg-red-600/10' : 'border-white/10 bg-[#0a0a0b] hover:border-red-700/40'}`}
                >
                  <div className="font-serif-editorial text-red-500 text-[18px] sm:text-[22px] leading-none">{c.code}</div>
                  <div className="text-white/80 text-[11.5px] sm:text-[12px] mt-1 sm:mt-1.5 font-semibold leading-tight">{c.title.split(' ').slice(0, 3).join(' ')}</div>
                  <div className="text-white/40 text-[10px] sm:text-[10.5px] mt-0.5 sm:mt-1 font-mono-spec">{c.lecons.length} leçons</div>
                </button>
              ))}
            </div>

            {/* CTA livret */}
            {user ? (
              <div className="mt-6 inline-flex items-center gap-3 text-[13px] text-white/65">
                <BookOpenCheck size={15} className="text-red-400" />
                <span>Connecté en tant que <span className="text-white font-semibold">{user.name}</span> — ta progression est synchronisée avec ton livret.</span>
                <Link to="/livret" className="text-red-400 hover:text-red-300 font-semibold">Voir mon livret →</Link>
              </div>
            ) : (
              <div className="mt-6 rounded-xl border border-amber-700/30 bg-amber-950/15 p-3.5 flex items-center gap-3 max-w-2xl">
                <GraduationCap size={16} className="text-amber-400 shrink-0" />
                <div className="text-[12.5px] text-white/75 leading-tight">
                  Connecte-toi pour suivre ta progression compétence par compétence et synchroniser ton livret.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter pill */}
        {filter !== 'all' && (
          <div className="mb-6 flex items-center gap-3" data-testid="active-filter-banner">
            <span className="text-[11.5px] uppercase tracking-wider text-white/40 font-mono-spec">Filtre actif :</span>
            <span className="px-3 py-1 rounded-full bg-red-600 text-white text-[11.5px] font-bold tracking-wider">{filter}</span>
            <button onClick={() => setFilter('all')} className="text-[12.5px] text-white/60 hover:text-white font-semibold">Voir toutes les compétences →</button>
          </div>
        )}

        {/* Compétences */}
        {visibleComps.map((c) => <CompetenceBlock key={c.code} comp={c} seanceIds={seanceIds} onToggleSeance={toggleSeance} revoirIds={revoirIds} onToggleRevoir={toggleRevoir} targetKey={targetKey} />)}

        {/* CTA vers la progression pratique */}
        <div className="mt-14 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-6" data-testid="cta-progression">
          <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300">
              <GraduationCap size={22} />
            </div>
            <div className="flex-1 min-w-0 basis-[calc(100%-4rem)] sm:basis-auto">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-bold font-mono-spec">Complément REMC</div>
              <h3 className="font-serif-editorial text-white text-[22px] sm:text-[26px] leading-tight mt-1">Ma progression pratique — 23 étapes</h3>
              <p className="text-white/70 text-[13.5px] mt-2 leading-relaxed max-w-2xl">
                {user?.role === 'enseignant' ? (
                  <>Le REMC c&apos;est le POURQUOI et le COMMENT. La progression c&apos;est QUAND enseigner quoi.
                  Trame en 3 phases (Mécanique · Observation · Préparation permis). Statue chaque étape en « acquise / en cours / à revoir »
                  et copie une séance prête à envoyer à ton élève.</>
                ) : (
                  <>Le REMC c&apos;est le POURQUOI et le COMMENT. Ta progression c&apos;est QUAND tu abordes quoi.
                  Parcours en 3 phases (Mécanique · Observation · Préparation permis). Marque chaque étape en « acquise / en cours / à revoir »
                  après chaque leçon pour voir ton évolution 🚀.</>
                )}
              </p>
            </div>
            <Link to="/cours/progression" className="h-10 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-[13px] font-bold inline-flex items-center justify-center gap-1.5 shrink-0 w-full sm:w-auto mt-3 sm:mt-0" data-testid="link-progression">
              Ouvrir ma progression <ArrowLeft size={13} className="rotate-180" />
            </Link>
          </div>
        </div>

        {/* Footer credits */}
        <div className="mt-16 mb-10 pt-8 border-t border-white/5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[11.5px] text-white/40 font-mono-spec inline-flex items-center gap-2">
            <ShieldCheck size={12} /> Aligné REMC · Pensé par des moniteurs ECSR titulaires · codevoiture.fr
          </div>
          <Link to="/cours-interactifs" data-testid="cours-eleve-back-btn" className="inline-flex items-center gap-1.5 text-[13px] text-red-400 hover:text-red-300 font-semibold">
            <ArrowLeft size={13} /> Retour aux outils interactifs
          </Link>
        </div>
      </section>

      {/* Barre flottante Séance du jour */}
      {seanceIds.size > 0 && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-40 w-[calc(100%-1.5rem)] sm:w-auto max-w-3xl"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)' }}
          data-testid="seance-floating-bar"
        >
          <div className="rounded-2xl border border-emerald-500/40 bg-[#0d1a10]/95 backdrop-blur-md shadow-2xl shadow-emerald-950/60 overflow-hidden">
            {/* Header row */}
            <div className="flex items-center gap-3 p-3 sm:p-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
                <ClipboardList size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-[13.5px] sm:text-[14.5px] leading-tight">
                  Séance du jour · <span className="text-emerald-300">{seanceIds.size} leçon{seanceIds.size > 1 ? 's' : ''}</span>
                </div>
                <div className="text-white/55 text-[11.5px] mt-0.5 line-clamp-1">
                  {selectedItems.slice(0, 3).map((it) => `${it.competenceCode}.${it.idx + 1}`).join(' · ')}
                  {selectedItems.length > 3 && ` · +${selectedItems.length - 3}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSeancePanelExpanded((v) => !v)}
                data-testid="seance-toggle-details"
                className="shrink-0 w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
                aria-label={seancePanelExpanded ? 'Réduire' : 'Voir le détail'}
              >
                <ChevronDown size={16} className={`transition-transform ${seancePanelExpanded ? '' : 'rotate-180'}`} />
              </button>
            </div>

            {/* Details (expanded) */}
            {seancePanelExpanded && (
              <div className="px-3 sm:px-4 pb-2 max-h-40 overflow-y-auto space-y-1">
                {selectedItems.map((it) => (
                  <div key={`${it.competenceCode}.${it.idx}`} className="flex items-center gap-2 text-[12.5px] text-white/75 py-1">
                    <span className="font-mono-spec text-emerald-400 font-bold w-11 shrink-0">{it.competenceCode}.{it.idx + 1}</span>
                    <span className="flex-1 truncate">{it.lecon.titre}</span>
                    <button
                      type="button"
                      onClick={() => toggleSeance(`${it.competenceCode}.${it.idx}`)}
                      className="shrink-0 w-6 h-6 rounded hover:bg-red-950/30 flex items-center justify-center text-white/50 hover:text-red-300 transition"
                      aria-label={`Retirer ${it.competenceCode}.${it.idx + 1}`}
                      data-testid={`seance-remove-${it.competenceCode}-${it.idx}`}
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* ═══ APERÇU STYLE WHATSAPP — voir le message avant envoi */}
            {seancePreviewOpen && (() => {
              const previewTxt = buildSeanceText(selectedItems, revoirIds);
              const now = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
              const copyFromPreview = async () => {
                const ok = await copyToClipboard(previewTxt);
                if (ok) { setPreviewCopied(true); sparkle(); setTimeout(() => setPreviewCopied(false), 2200); }
              };
              return (
                <div className="border-t border-emerald-500/15" data-testid="seance-preview-panel">
                  <div className="flex items-center gap-2.5 px-3 sm:px-4 py-2 bg-[#1f2c34]">
                    <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-white shrink-0">
                      <GraduationCap size={13} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-[12.5px] font-semibold leading-tight">Ton élève</div>
                      <div className="text-white/45 text-[10px]">aperçu du message avant envoi</div>
                    </div>
                    <button type="button" onClick={() => setSeancePreviewOpen(false)} data-testid="seance-preview-close" className="w-7 h-7 rounded-full text-white/50 hover:text-white hover:bg-white/10 inline-flex items-center justify-center">
                      <X size={13} />
                    </button>
                  </div>
                  <div className="px-3 sm:px-4 py-3 max-h-[38vh] overflow-y-auto" style={{ background: '#0b141a', backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '18px 18px' }}>
                    <div className="flex justify-end">
                      <div className="relative max-w-[92%] sm:max-w-[85%] rounded-xl rounded-tr-sm px-3 py-2 shadow-md" style={{ background: '#005c4b' }} data-testid="seance-preview-bubble">
                        <pre className="whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed text-white/95 m-0">{previewTxt}</pre>
                        <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-white/55">
                          {now}
                          <Check size={12} className="text-sky-300 -mr-2" /><Check size={12} className="text-sky-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 px-3 sm:px-4 py-2.5 bg-[#1f2c34]">
                    <span className="flex-1 text-white/45 text-[11px] leading-snug hidden sm:block">Vérifie le contenu, puis envoie-le ou copie-le.</span>
                    <button type="button" onClick={copyFromPreview} data-testid="seance-preview-copy" className={`h-9 px-3.5 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 transition ${previewCopied ? 'bg-emerald-500 text-emerald-950' : 'bg-white/10 hover:bg-white/15 text-white border border-white/15'}`}>
                      {previewCopied ? <><Check size={12} /> Copié !</> : <><Copy size={12} /> Copier</>}
                    </button>
                    <button type="button" onClick={smsSeance} data-testid="seance-preview-sms" className="h-9 px-3.5 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white transition">
                      <MessageSquare size={12} /> SMS
                    </button>
                    <button type="button" onClick={whatsappSeance} data-testid="seance-preview-whatsapp" className="h-9 px-3.5 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 text-white transition" style={{ background: '#25D366' }}>
                      <Send size={12} /> WhatsApp
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Action row */}
            <div className="flex flex-wrap items-center gap-2 p-3 sm:p-4 pt-2 border-t border-emerald-500/15 bg-black/20">
              <button
                type="button"
                onClick={() => setSeancePreviewOpen((v) => !v)}
                data-testid="seance-preview-toggle"
                className={`flex-1 min-w-[150px] inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full text-[13px] sm:text-[13.5px] font-semibold transition ${
                  seancePreviewOpen
                    ? 'bg-emerald-500 text-emerald-950'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-950/40'
                }`}
                title="Voir le message exactement comme il apparaîtra sur WhatsApp / SMS avant de l'envoyer"
              >
                {seancePreviewOpen ? <><EyeOff size={15} /> Masquer l'aperçu</> : <><Eye size={15} /> Aperçu du message ({seanceIds.size})</>}
              </button>
              <button
                type="button"
                onClick={copySeance}
                data-testid="seance-copy-all"
                className={`shrink-0 h-11 px-3 rounded-full border text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition ${
                  seanceCopied
                    ? 'bg-emerald-500 text-black border-emerald-500'
                    : 'border-white/15 text-white/85 hover:text-emerald-200 hover:border-emerald-500/50 hover:bg-emerald-950/20'
                }`}
                aria-label="Copier la séance"
                title="Copier la séance"
              >
                {seanceCopied ? <><Check size={14} /> Copié !</> : <><Copy size={14} /> Copier</>}
              </button>
              <button
                type="button"
                onClick={smsSeance}
                data-testid="seance-sms-all"
                className="shrink-0 h-11 px-3 rounded-full border border-white/15 text-white/85 hover:text-sky-200 hover:border-sky-500/50 hover:bg-sky-950/20 text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition"
                aria-label="Envoyer la séance par SMS"
                title="Envoyer la séance par SMS"
              >
                <MessageSquare size={14} /> SMS
              </button>
              <button
                type="button"
                onClick={whatsappSeance}
                data-testid="seance-whatsapp-all"
                className="shrink-0 h-11 px-3 rounded-full border border-white/15 text-white/85 hover:text-[#25D366] hover:border-[#25D366]/60 hover:bg-[#25D366]/10 text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition"
                aria-label="Envoyer la séance par WhatsApp"
                title="Envoyer la séance par WhatsApp"
              >
                <Send size={14} /> WhatsApp
              </button>
              <button
                type="button"
                onClick={clearSeance}
                data-testid="seance-clear"
                className="shrink-0 h-11 px-3 sm:px-4 rounded-full border border-white/15 text-white/75 hover:text-white hover:border-red-700/40 hover:bg-red-950/20 text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition"
                aria-label="Vider la séance"
              >
                <X size={14} /> <span className="hidden sm:inline">Vider</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
